import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  Button,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { SiteAnalytics } from '../../types/analytics';
import { useSites } from '../../hooks/useSites';
import { AnalyticsService } from '../../services/analyticsService';
import { useAuth } from '../../contexts/AuthContext';
import { getDisplayUrl, getPreviewUrl } from '../../utils/urlHelper';
import { NotFound } from './NotFound';
import { AnalyticsHeader } from './analytics/AnalyticsHeader';
import { AnalyticsStatsCards } from './analytics/AnalyticsStatsCards';
import { AnalyticsChart } from './analytics/AnalyticsChart';
import { AnalyticsCTAButtonTable } from './analytics/AnalyticsCTAButtonTable';
import { AnalyticsPageTable } from './analytics/AnalyticsPageTable';

export const Analytics: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { sites } = useSites();
  const { currentUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<SiteAnalytics | null>(null);
  // デフォルト: 表示期間は今月、比較期間は前月
  const getThisMonthDates = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const getLastMonthDates = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const thisMonth = getThisMonthDates();
  const lastMonth = getLastMonthDates();

  const [startDate, setStartDate] = useState<Date>(thisMonth.start);
  const [endDate, setEndDate] = useState<Date>(thisMonth.end);
  const [comparisonStartDate, setComparisonStartDate] = useState<Date>(lastMonth.start);
  const [comparisonEndDate, setComparisonEndDate] = useState<Date>(lastMonth.end);
  const [comparisonAnalyticsData, setComparisonAnalyticsData] = useState<SiteAnalytics | null>(null);
  const [viewType, setViewType] = useState<'daily' | 'monthly'>('daily');
  
  // ページ別分析表の並び替え状態
  const [pageSortField, setPageSortField] = useState<string>('');
  const [pageSortDirection, setPageSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleBack = () => {
    navigate('/admin/sites');
  };

  const handleResetData = async () => {
    if (!siteId) return;
      
      // Firestoreからこのサイトの分析データを削除
      const { collection, query, where, getDocs, deleteDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../../firebase/config');
      
      // analytics_eventsを削除
      const eventsRef = collection(db, 'analytics_events');
      const eventsQuery = query(eventsRef, where('siteId', '==', siteId));
      const eventsSnapshot = await getDocs(eventsQuery);
      
      const deleteEventsPromises = eventsSnapshot.docs.map(document => 
        deleteDoc(doc(db, 'analytics_events', document.id))
      );
      
      // analytics_sessionsを削除
      const sessionsRef = collection(db, 'analytics_sessions');
      const sessionsQuery = query(sessionsRef, where('siteId', '==', siteId));
      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      const deleteSessionsPromises = sessionsSnapshot.docs.map(document => 
        deleteDoc(doc(db, 'analytics_sessions', document.id))
      );
      
      await Promise.all([...deleteEventsPromises, ...deleteSessionsPromises]);
      window.location.reload();
  };

  useEffect(() => {
    const loadAnalyticsData = async () => {
      try {
        const hostname = window.location.hostname;
        if (hostname === 'preview.qwipe.jp' || hostname === 'www.preview.qwipe.jp') {
          setError('プレビューモードでは分析データを表示できません');
          return;
        }

        setError(null);
        
        if (!siteId) {
          setError('サイトIDが指定されていません');
          return;
        }
        
        if (sites.length === 0) {
          return;
        }
        
        const currentSite = sites.find(site => site.id === siteId);
        if (!currentSite) {
          setError('サイトが見つかりません');
          return;
        }
        
        if (currentUser?.role === 'viewer' && currentSite.companyUserId !== currentUser.id) {
          setError('このサイトにアクセスする権限がありません');
          return;
        }
        
        const data = await AnalyticsService.getSiteAnalytics(
          { siteId, startDate, endDate },
          currentSite
        );
        setAnalyticsData(data);
        
        try {
          const comparisonData = await AnalyticsService.getSiteAnalytics(
            { siteId, startDate: comparisonStartDate, endDate: comparisonEndDate },
            currentSite
          );
          setComparisonAnalyticsData(comparisonData);
        } catch (comparisonErr) {
          setComparisonAnalyticsData(null);
        }
      } catch (err) {
        setError('分析データの読み込みに失敗しました');
      }
    };

    loadAnalyticsData();
  }, [siteId, startDate, endDate, comparisonStartDate, comparisonEndDate, sites, currentUser]);

  const currentSite = sites.find(site => site.id === siteId);
  
  const [chartData, setChartData] = useState<Array<{
    date: string;
    pv: number;
    uu: number;
    totalSwipes: number;
    cv: number;
    cvr: number;
    ctaClicks: number;
  }>>([]);

  useEffect(() => {
    const loadChartData = async () => {
      if (!currentSite || !siteId) {
        setChartData([]);
        return;
      }

      try {
        const sessions = await AnalyticsService.getSessions({
          siteId,
          startDate,
          endDate,
        });

        const pages = currentSite.pages || [];
        const lastPageId = pages.length > 0 ? (pages[pages.length - 1].id || pages[pages.length - 1].pageId) : null;

        const dataMap = new Map<string, {
          date: string;
          pv: number;
          uu: Set<string>;
          totalSwipes: number;
          cv: number;
          ctaClicks: number;
        }>();

        if (viewType === 'daily') {
          const currentDate = new Date(startDate);
          while (currentDate <= endDate) {
            const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
            const dateLabel = `${currentDate.getMonth() + 1}/${currentDate.getDate()}`;
            dataMap.set(dateKey, {
              date: dateLabel,
            pv: 0,
            uu: new Set(),
            totalSwipes: 0,
            cv: 0,
            ctaClicks: 0,
          });
            currentDate.setDate(currentDate.getDate() + 1);
          }
        } else {
          const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
          const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
          while (currentDate <= endMonth) {
            const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
            const monthLabel = `${currentDate.getMonth() + 1}月`;
            dataMap.set(monthKey, {
              date: monthLabel,
              pv: 0,
              uu: new Set(),
              totalSwipes: 0,
              cv: 0,
              ctaClicks: 0,
            });
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
        }

        sessions.forEach(session => {
          const sessionDate = session.startTime;
          let key: string;
          
          if (viewType === 'daily') {
            key = `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}-${String(sessionDate.getDate()).padStart(2, '0')}`;
          } else {
            key = `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}`;
          }
          
          const data = dataMap.get(key);
          if (data) {
            data.pv++;
            data.uu.add(session.sessionId);
            if (session.pageVisits && Array.isArray(session.pageVisits)) {
              data.totalSwipes += session.pageVisits.length;
            }
            if (lastPageId && session.pagesVisited && session.pagesVisited.includes(lastPageId)) {
              data.cv++;
            }
            data.ctaClicks += session.ctaClicks || 0;
          }
        });

        const formattedData = Array.from(dataMap.values()).map(data => ({
          date: data.date,
          pv: data.pv,
          uu: data.uu.size,
          totalSwipes: data.totalSwipes,
          cv: data.cv,
          cvr: data.pv > 0 ? (data.cv / data.pv) * 100 : 0,
          ctaClicks: data.ctaClicks,
        }));

        setChartData(formattedData);
      } catch (error) {
        console.error('チャートデータの集計エラー:', error);
        setChartData([]);
      }
    };

    loadChartData();
  }, [currentSite, siteId, startDate, endDate, viewType]);
  
  // ページ別分析表の並び替えハンドラー
  const handlePageSort = (field: string) => {
    if (pageSortField === field) {
      setPageSortDirection(pageSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setPageSortField(field);
      setPageSortDirection('asc');
    }
  };

  if (error) {
    // 閲覧者の権限エラーの場合は404ページを表示
    if (error.includes('アクセスする権限がありません')) {
      return <NotFound />;
    }
    
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          再読み込み
        </Button>
      </Box>
    );
  }
  
  // 閲覧者の場合、サイトが自分のcompanyUserIdと一致しない場合は404ページを表示
  if (currentUser?.role === 'viewer' && currentSite && currentSite.companyUserId !== currentUser.id) {
    return <NotFound />;
  }
  
    const siteUrl = currentSite?.customDomain
      ? getDisplayUrl(currentSite.customDomain)
      : currentSite?.id
        ? getPreviewUrl(currentSite.id)
        : null;

  return (
    <Box sx={{ backgroundColor: '#F5F5F5', minHeight: '100vh' }}>
      <AnalyticsHeader
        siteTitle={currentSite?.title}
        siteUrl={siteUrl}
        startDate={startDate}
        endDate={endDate}
        comparisonStartDate={comparisonStartDate}
        comparisonEndDate={comparisonEndDate}
        viewType={viewType}
        onBack={handleBack}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onComparisonStartDateChange={setComparisonStartDate}
        onComparisonEndDateChange={setComparisonEndDate}
        onViewTypeChange={setViewType}
        onResetData={handleResetData}
      />

      <AnalyticsChart chartData={chartData} viewType={viewType} />

      <AnalyticsStatsCards
        analyticsData={analyticsData}
        comparisonAnalyticsData={comparisonAnalyticsData}
      />

      <AnalyticsCTAButtonTable
        currentSite={currentSite}
        analyticsData={analyticsData}
        comparisonAnalyticsData={comparisonAnalyticsData}
      />

      <AnalyticsPageTable
        analyticsData={analyticsData}
        comparisonAnalyticsData={comparisonAnalyticsData}
        currentSite={currentSite}
        pageSortField={pageSortField}
        pageSortDirection={pageSortDirection}
        onPageSort={handlePageSort}
      />
    </Box>
  );
};
